import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setActiveSessionFlag, isExamOrPracticeActive } from "@/lib/safe-storage";
import { versionWatcher } from "@/lib/version-watcher";

interface ExamSessionContextType {
  examInProgress: boolean;
  activeExamId: string | null;
  startExam: (examId: string) => void;
  endExam: () => void;
}

const ExamSessionContext = createContext<ExamSessionContextType>({
  examInProgress: false,
  activeExamId: null,
  startExam: () => {},
  endExam: () => {},
});

const POST_SESSION_RESUME_DELAY_MS = 4_000;

export const ExamSessionProvider = ({ children }: { children: ReactNode }) => {
  const [activeExamId, setActiveExamId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const prevActiveRef = useRef<boolean>(false);

  const startExam = useCallback((examId: string) => {
    console.log("🛡️ [ExamSession] Exam started, locking session:", examId);
    setActiveExamId(examId);
  }, []);

  const endExam = useCallback(() => {
    console.log("🛡️ [ExamSession] Exam ended, unlocking session");
    setActiveExamId(null);
  }, []);

  // Mirror state to a window flag so non-React modules
  // (version-watcher) can guard hard reloads.
  useEffect(() => {
    const active = activeExamId !== null;
    setActiveSessionFlag(active);

    // When a session ends, ask the watcher to re-check — if an update was
    // deferred while the exam was running it will now be applied.
    if (prevActiveRef.current && !active) {
      const pending = versionWatcher.getState().pendingReload;
      if (pending) {
        console.log("🔄 [ExamSession] Pending update — scheduling re-check after exam", {
          delayMs: POST_SESSION_RESUME_DELAY_MS,
        });
        window.setTimeout(() => {
          if (isExamOrPracticeActive()) return;
          versionWatcher
            .forceCheck({ reason: "post-session-resume", allowReload: true })
            .catch((err) => console.warn("[ExamSession] post-session forceCheck failed:", err));
        }, POST_SESSION_RESUME_DELAY_MS);
      }
    }
    prevActiveRef.current = active;

    return () => setActiveSessionFlag(false);
  }, [activeExamId, queryClient]);

  return (
    <ExamSessionContext.Provider
      value={{
        examInProgress: activeExamId !== null,
        activeExamId,
        startExam,
        endExam,
      }}
    >
      {children}
    </ExamSessionContext.Provider>
  );
};

export const useExamSession = () => useContext(ExamSessionContext);
